import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-all disabled:pointer-events-none disabled:bg-grayscale-20 disabled:text-grayscale-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-grayscale-100 text-white hover:bg-grayscale-100/90",
        primary: "bg-primary text-white hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "bg-white shadow-xs hover:bg-white hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-grayscale-30 text-white hover:bg-grayscale-10",
        thirdary:
          "bg-grayscale-70 text-white hover:bg-grayscale-60",
        ghost: "text-grayscale-50 bg-transparent hover:bg-transparent hover:text-grayscale-50/80 disabled:bg-transparent",
        link: "underline underline-offset-4",
        border: "bg-white border border-grayscale-10 hover:bg-white/90",
        counter: "bg-grayscale-60 text-white rounded-sm text-xs"
      },
      size: {
        default: "py-2 px-3",
        xs_12: "py-1 px-3 text-xs has-[>svg]:px-2",
        sm_12: "py-2.5 px-5 text-xs has-[>svg]:px-2",
        sm_14: "py-2 px-8 text-sm has-[>svg]:px-2",
        md_14: "py-3 px-11 text-sm has-[>svg]:px-3",
        lg_14: "py-3 px-20 text-sm has-[>svg]:px-3",
        xs: "py-2 px-6 text-sm font-medium has-[>svg]:px-2",
        sm: "py-3 px-6 text-base font-medium has-[>svg]:px-2",
        md: "py-3 px-9 text-base font-medium has-[>svg]:px-3",
        lg: "py-3 px-12 text-base has-[>svg]:px-5",
        icon: "size-6",
        sm_icon: "size-4"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, type, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        type={type || "button"}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
